import { supabase } from '@/integrations/supabase/client';

export interface PageContent {
  id: string;
  pageSlug: string;
  contentKey: string;
  contentValue: string;
  contentType: 'text' | 'html' | 'image' | 'color' | 'json';
  section: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ContentRevision {
  id: string;
  pageSlug: string;
  contentKey: string;
  oldValue: string | null;
  newValue: string;
  changedBy: string | null;
  createdAt: string;
}

export interface SiteConfig {
  id: string;
  configKey: string;
  configValue: string;
  configType: 'text' | 'html' | 'image' | 'color' | 'json' | 'boolean';
  category: string;
  label: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

function mapPageContent(item: any): PageContent {
  return {
    id: item.id,
    pageSlug: item.page_slug,
    contentKey: item.content_key,
    contentValue: item.content_value,
    contentType: item.content_type,
    section: item.section,
    sortOrder: item.sort_order,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

function mapSiteConfig(item: any): SiteConfig {
  return {
    id: item.id,
    configKey: item.config_key,
    configValue: item.config_value,
    configType: item.config_type,
    category: item.category,
    label: item.label,
    description: item.description,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

function mapRevision(item: any): ContentRevision {
  return {
    id: item.id,
    pageSlug: item.page_slug,
    contentKey: item.content_key,
    oldValue: item.old_value,
    newValue: item.new_value,
    changedBy: item.changed_by,
    createdAt: item.created_at,
  };
}

export async function getPageContent(pageSlug: string): Promise<PageContent[]> {
  const { data, error } = await supabase
    .from('page_content')
    .select('*')
    .eq('page_slug', pageSlug)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapPageContent);
}

export async function getPageContentByKey(pageSlug: string, contentKey: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('page_content')
    .select('content_value')
    .eq('page_slug', pageSlug)
    .eq('content_key', contentKey)
    .single();

  if (error) return null;
  return data?.content_value || null;
}

export async function updatePageContent(
  pageSlug: string,
  contentKey: string,
  contentValue: string,
  contentType: string = 'text',
  section: string = 'general'
): Promise<PageContent> {
  const { data: updateData, error: updateError } = await supabase
    .from('page_content')
    .update({
      content_value: contentValue,
      content_type: contentType,
      section: section,
      updated_at: new Date().toISOString(),
    })
    .eq('page_slug', pageSlug)
    .eq('content_key', contentKey)
    .select()
    .single();

  if (updateError && updateError.code === 'PGRST116') {
    const { data: insertData, error: insertError } = await supabase
      .from('page_content')
      .insert({
        page_slug: pageSlug,
        content_key: contentKey,
        content_value: contentValue,
        content_type: contentType,
        section: section,
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (insertError) throw insertError;
    return mapPageContent(insertData);
  }

  if (updateError) throw updateError;
  return mapPageContent(updateData);
}

export async function deletePageContent(id: string): Promise<void> {
  const { error } = await supabase.from('page_content').delete().eq('id', id);
  if (error) throw error;
}

export interface PageContentUpdate {
  pageSlug: string;
  contentKey: string;
  contentValue: string;
  contentType?: string;
  section?: string;
}

export async function bulkUpdatePageContent(items: PageContentUpdate[]): Promise<void> {
  for (const item of items) {
    await updatePageContent(
      item.pageSlug,
      item.contentKey,
      item.contentValue,
      item.contentType || 'text',
      item.section || 'general'
    );
  }
}

export async function getContentRevisions(
  pageSlug: string,
  contentKey?: string
): Promise<ContentRevision[]> {
  let query = supabase
    .from('content_revisions')
    .select('*')
    .eq('page_slug', pageSlug)
    .order('created_at', { ascending: false });

  if (contentKey) {
    query = query.eq('content_key', contentKey);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapRevision);
}

export async function restoreRevision(revisionId: string): Promise<void> {
  const { data: revision, error: fetchError } = await supabase
    .from('content_revisions')
    .select('*')
    .eq('id', revisionId)
    .single();

  if (fetchError) throw fetchError;

  await updatePageContent(
    revision.page_slug,
    revision.content_key,
    revision.old_value || ''
  );
}

export async function getSiteConfig(): Promise<SiteConfig[]> {
  const { data, error } = await supabase
    .from('site_config')
    .select('*')
    .order('category', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapSiteConfig);
}

export async function updateSiteConfig(
  configKey: string,
  configValue: string,
  configType: string = 'text'
): Promise<SiteConfig> {
  const { data: updateData, error: updateError } = await supabase
    .from('site_config')
    .update({
      config_value: configValue,
      config_type: configType,
      updated_at: new Date().toISOString(),
    })
    .eq('config_key', configKey)
    .select()
    .single();

  if (updateError && updateError.code === 'PGRST116') {
    const { data: insertData, error: insertError } = await supabase
      .from('site_config')
      .insert({
        config_key: configKey,
        config_value: configValue,
        config_type: configType,
        category: 'general',
        label: configKey,
        description: '',
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (insertError) throw insertError;
    return mapSiteConfig(insertData);
  }

  if (updateError) throw updateError;
  return mapSiteConfig(updateData);
}
